import React, { useCallback, useEffect, useState } from 'react';
import { Sun, SunMedium, CloudSun, Cloud, CloudLightning, Loader2, Check, Pencil, HeartHandshake } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { easerGet, easerPost, errorMessage } from '../../lib/easerApi';

export const MOODS = [
  { id: 'sun', label: 'Grand soleil', icon: Sun, color: 'text-[hsl(var(--brand-apricot))]', active: 'bg-[hsl(var(--brand-apricot))]/15 border-[hsl(var(--brand-apricot))]' },
  { id: 'sun_light', label: 'Soleil', icon: SunMedium, color: 'text-[hsl(var(--brand-apricot))]/80', active: 'bg-[hsl(var(--brand-apricot))]/10 border-[hsl(var(--brand-apricot))]/70' },
  { id: 'cloud_sun', label: 'Nuage et soleil', icon: CloudSun, color: 'text-[hsl(var(--primary))]', active: 'bg-[hsl(var(--primary))]/10 border-[hsl(var(--primary))]' },
  { id: 'cloud', label: 'Nuage', icon: Cloud, color: 'text-muted-foreground', active: 'bg-secondary border-foreground/40' },
  { id: 'storm', label: 'Éclairs', icon: CloudLightning, color: 'text-destructive', active: 'bg-destructive/10 border-destructive' },
];
const NEEDS_TEXT = ['cloud', 'storm'];

/** Accueil: daily mood check-in ("Comment te sens-tu aujourd'hui ?"), one answer per day, editable the same day. */
export function EaserMoodCard({ onUnauthorized }) {
  const [state, setState] = useState(null); // {day, answer, missing}
  const [mood, setMood] = useState(null);
  const [feeling, setFeeling] = useState('');
  const [improve, setImprove] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await easerGet('/api/easer/moral/today');
      setState(res.data);
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized?.();
      setState({ answer: null, missing: true, offline: true });
    }
  }, [onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const pick = async (id) => {
    setMood(id);
    if (!NEEDS_TEXT.includes(id)) await submit(id, '', '');
  };

  const submit = async (id = mood, f = feeling, i = improve) => {
    if (!id) return;
    if (NEEDS_TEXT.includes(id) && !f.trim()) return toast.error('Dis-nous en quelques mots ce que tu ressens');
    setSaving(true);
    try {
      const res = await easerPost('/api/easer/moral', { mood: id, feeling: f.trim(), improve: i.trim() });
      setState({ day: res.data.answer.day, answer: res.data.answer, missing: false });
      setEditing(false);
      toast.success(NEEDS_TEXT.includes(id) ? 'Merci pour ta confiance, l\'agence est prévenue et reviendra vers toi.' : 'Merci ! Belle journée.');
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized?.();
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = () => {
    const a = state?.answer;
    setMood(a?.mood || null); setFeeling(a?.feeling || ''); setImprove(a?.improve || '');
    setEditing(true);
  };

  if (state === null) return null;
  const answer = state.answer;
  const showForm = !answer || editing;
  const current = MOODS.find((m) => m.id === (showForm ? mood : answer?.mood));

  return (
    <Card className="p-4 border-border shadow-sm" data-testid="easer-mood-card">
      <div className="flex items-start gap-3">
        <span className="h-10 w-10 rounded-xl bg-[hsl(var(--brand-mint))] text-[hsl(var(--primary))] flex items-center justify-center flex-shrink-0"><HeartHandshake className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          {!showForm ? (
            <div className="flex items-start justify-between gap-2" data-testid="mood-answered">
              <div>
                <p className="font-semibold flex items-center gap-1.5">{current && <current.icon className={`h-5 w-5 ${current.color}`} />} Aujourd'hui : {current?.label.toLowerCase()}</p>
                <p className="text-sm text-muted-foreground mt-0.5">{NEEDS_TEXT.includes(answer.mood) ? 'Merci de nous l\'avoir dit, l\'agence est prévenue.' : 'Merci d\'avoir partagé ta météo du jour.'}</p>
              </div>
              <Button size="sm" variant="ghost" className="h-9 flex-shrink-0" onClick={startEdit} data-testid="mood-edit"><Pencil className="h-4 w-4 mr-1" /> Modifier</Button>
            </div>
          ) : (
            <>
              <p className="font-semibold">Comment te sens-tu aujourd'hui ?</p>
              <p className="text-xs text-muted-foreground mt-0.5">Ta météo du moral aide l'agence à prendre soin de toi.</p>
              <div className="grid grid-cols-5 gap-1.5 mt-3" role="radiogroup" aria-label="Météo du moral" data-testid="mood-options">
                {MOODS.map((m) => (
                  <button key={m.id} type="button" role="radio" aria-checked={mood === m.id} onClick={() => pick(m.id)} disabled={saving} data-testid={`mood-${m.id}`}
                    className={`flex flex-col items-center justify-start gap-1 rounded-xl border px-1 py-2 min-h-[68px] text-[10px] font-medium leading-tight text-center transition-colors ${mood === m.id ? m.active : 'border-border hover:bg-secondary'}`}>
                    <m.icon className={`h-6 w-6 ${m.color}`} /> <span className="break-words">{m.label}</span>
                  </button>
                ))}
              </div>
              {mood && NEEDS_TEXT.includes(mood) && (
                <div className="mt-3 space-y-3" data-testid="mood-details">
                  <div className="space-y-1">
                    <Label htmlFor="mood-feeling">Exprime ton ressenti</Label>
                    <Textarea id="mood-feeling" rows={3} value={feeling} onChange={(e) => setFeeling(e.target.value)} placeholder="Ce qui pèse aujourd'hui…" maxLength={2000} data-testid="mood-feeling" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="mood-improve">Comment peut-on améliorer ta situation ?</Label>
                    <Textarea id="mood-improve" rows={2} value={improve} onChange={(e) => setImprove(e.target.value)} placeholder="Une idée, un besoin, un changement…" maxLength={2000} data-testid="mood-improve" />
                  </div>
                  <div className="flex gap-2">
                    {editing && <Button variant="outline" className="h-11" onClick={() => setEditing(false)} disabled={saving} data-testid="mood-cancel">Annuler</Button>}
                    <Button className="h-11 flex-1 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" onClick={() => submit()} disabled={saving} data-testid="mood-submit">
                      {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />} Envoyer à l'agence
                    </Button>
                  </div>
                </div>
              )}
              {saving && !(mood && NEEDS_TEXT.includes(mood)) && <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Enregistrement…</p>}
              {editing && !(mood && NEEDS_TEXT.includes(mood)) && <Button variant="ghost" size="sm" className="mt-2 h-9" onClick={() => setEditing(false)} data-testid="mood-cancel">Annuler</Button>}
            </>
          )}
        </div>
      </div>
    </Card>
  );
}
